import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { Button } from '../../Components/UI/button';
import { Input } from '../../Components/UI/input';
import { Label } from '../../Components/UI/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from '../../Components/UI/dialog';
import { Textarea } from '../../Components/UI/textarea';
import { Plus, X, ArrowLeft, Check, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import CustomSearchableDropdown from '../../Components/Common/CustomSearchableDD';
import { DatePicker } from '../../Components/Common/DatePicker';
import { capitalizeFirstLetter, combineDateTime, getMobileHeaderTitle } from '../../Utils/Utilities';
import useIsMobile from '../../Hooks/useIsMobile';
import { AlertBox } from '../../Components/Common/AlertBox';
import { setExpensesDataAPI } from './ExpensesAPI';
import { useDispatch } from 'react-redux';
import { triggerExpensesRefresh } from '../../Store/Reducers';

// Expense categories
const expenseCategories = [
    { label: 'Awareness / Marketing', value: 1 },
    { label: 'New Conversions', value: 2 },
    { label: 'Repeat Business', value: 3 },
    { label: 'Order / Delivery', value: 4 }
];

// Main Expense Entry Component
const ExpenseEntry = forwardRef(
    (
        {
            dialogTitle = "Expense Entry",
            triggerButton = true,
            triggerText = "Add Expense",
            open: externalOpen,
            onOpenChange: externalOnOpenChange,
            prefilledData = null,
            onExpenseSet,
            extFieldUse = false,
            defaultMonth,
            defaultYear,
        },
        ref
    ) => {
        // Get today's date in dd-MMM-yy format
        const today = new Date();
        const todayYear = today.getFullYear();
        const todayMonth = today.getMonth() + 1;

        // logic to determine default date value for date picker
        const defaultDTValue = !defaultMonth || !defaultYear
        ? format(today, 'dd-MMM-yy')
        : (Number(defaultYear) === todayYear && Number(defaultMonth) === todayMonth)
        ? format(today, 'dd-MMM-yy')
        : format(new Date(Number(defaultYear), Number(defaultMonth) - 1, 1), 'dd-MMM-yy');

        const defaultMonthYearString = !defaultMonth || !defaultYear ? '' : `${defaultYear}-${defaultMonth}`;

        const isMobile = useIsMobile();
        const dispatch = useDispatch();

        // Initial object of expense data fields
        const initialFormData = {
            expenseDT: prefilledData?.expenseDT ? format(new Date(prefilledData?.expenseDT), 'dd-MMM-yy') : defaultDTValue,
            expenseTypeID: prefilledData?.expenseTypeID || 1,
            amount: prefilledData?.amount || '',
            notes: prefilledData?.notes || '',
        }
        let isEditMode = false; 
        if (prefilledData && prefilledData.expenseID && prefilledData.expenseID > 0) {
            isEditMode = true;
        }

        // Local state variables
        const [internalOpen, setInternalOpen] = useState(false);
        const [expenseFormData, setExpenseFormData] = useState(initialFormData);
        const isOpen = externalOpen !== undefined ? externalOpen : internalOpen;
        const setIsOpen = externalOnOpenChange || setInternalOpen;
        const [isExtFieldUse, setIsExtFieldUse] = useState(extFieldUse ? extFieldUse : false);
        const [isDeleteAlertOpen, setIsDeleteAlertOpen] = useState(false);

        // Updates form data when prefilledData changes
        useEffect(() => {
            if (prefilledData && isOpen) {
                const updatedFormData = {
                    expenseDT: prefilledData?.expenseDT ? format(new Date(prefilledData?.expenseDT), 'dd-MMM-yy') : defaultDTValue,
                    expenseTypeID: prefilledData?.expenseTypeID || 1,
                    amount: prefilledData?.amount || '',
                    notes: prefilledData?.notes || '',
                    status: 1
                };
                setExpenseFormData(updatedFormData);
            } else if (isOpen) {
                setExpenseFormData(initialFormData);
            }
        }, [prefilledData, isOpen, defaultDTValue]);

        // Get expense data by using reference
        useImperativeHandle(ref, () => ({
            getExtExpenseData: () => {
                return { validation: validateExpenseFormData(expenseFormData), data: expenseFormData };
            }
        }))

        // Handle every fields
        const handleFieldChange = (fieldName, value) => {
            // Handling for date field validations
            if (fieldName === "expenseDT") {
                const resultDT = new Date(value);

                // Check if date is in future
                if (resultDT > new Date()) {
                    toast.error("Invalid Date", { 
                        description: "Please select today or a past date. Future dates are not allowed.",
                        duration: 4000
                    });
                    setExpenseFormData((prev) => ({ 
                        ...prev, 
                        expenseDT: prefilledData?.expenseDT ? format(new Date(prefilledData?.expenseDT), 'dd-MMM-yy') : defaultDTValue 
                    }));
                    return;
                }
            }

            // Handle amount field - only allow numbers and decimals
            if (fieldName === "amount") {
                const numericValue = value.replace(/[^0-9.]/g, '');
                const parts = numericValue.split('.');
                const formattedValue = parts.length > 2 
                    ? parts[0] + '.' + parts.slice(1).join('') 
                    : numericValue;
                
                setExpenseFormData(prev => ({
                    ...prev,
                    [fieldName]: formattedValue
                }));
                return;
            }

            setExpenseFormData(prev => ({
                ...prev,
                [fieldName]: value
            }));
        };

        // Handle dropdown select fields
        const handleDropdownChange = (fieldName, option, value) => {
            setExpenseFormData(prev => ({
                ...prev,
                [fieldName]: option?.value
            }))
        }

        // Validate expense form data
        const validateExpenseFormData = (data) => {
            if (!data.expenseDT) {
                return {
                    valid: false,
                    error: {
                        title: "Please select a date.",
                        description: "Date is required."
                    }
                };
            } else if (!data.expenseTypeID) {
                return {
                    valid: false,
                    error: {
                        title: "Please select a category.",
                        description: "Category is required."
                    }
                };
            } else if (!data.amount || parseFloat(data.amount) <= 0) {
                return {
                    valid: false,
                    error: {
                        title: "Please enter a valid amount.",
                        description: "Amount must be greater than 0."
                    }
                };
            }

            return { valid: true };
        };

        // Handle save expense 
        const handleSaveExpense = async () => {
            const validation = validateExpenseFormData(expenseFormData);

            if (!validation.valid) {
                toast.error(validation.error.title, {
                    description: validation.error.description,
                    duration: 4000,
                });
                return;
            }

            const apiData = {
                ...expenseFormData,
                expenseID: prefilledData?.expenseID || 0,
                amount: parseFloat(expenseFormData.amount)
            };

            try {
                const operation = apiData.expenseID && apiData.expenseID > 0 ? 'UPD' : 'ADD';
                const result = await setExpensesDataAPI(apiData, operation);

                toast.success(
                    operation === 'UPD' ? "Expense updated." : "Expense added.",
                    {}
                );

                // Reset form
                setExpenseFormData(initialFormData);
                setIsOpen(false);

                dispatch(triggerExpensesRefresh());

                // Call the callback if provided
                if (onExpenseSet && typeof onExpenseSet === 'function') {
                    onExpenseSet();
                }
            } catch (error) {
                console.error(`Error in saving the expense:`, error);
                const errorMessage = error.isBusinessLogicError
                    ? error.message
                    : error.message || 'An error occurred while saving the expense';
                toast.error(errorMessage, { 
                    description: "Please try again or contact support if the issue persists.", 
                    duration: 4000 
                });
            }
        };

        const handleDeleteExpense = async () => {
            if (!prefilledData?.expenseID) {
                toast.error("Invalid expense ID");
                return;
            }

            try {
                const apiData = {
                    expenseID: prefilledData.expenseID,
                    expenseDT: format(prefilledData.expenseDT, 'dd-MMM-yy'),
                    amount: prefilledData.amount,
                    expenseTypeID: prefilledData.expenseTypeID,
                    notes: prefilledData.notes || ''
                };

                const result = await setExpensesDataAPI(apiData, 'DEL');
                console.log(result)

                toast.success("Expense deleted.", {});

                // Reset form
                setExpenseFormData(initialFormData);
                setIsOpen(false);

                dispatch(triggerExpensesRefresh());

                // Call the callback if provided
                if (onExpenseSet && typeof onExpenseSet === 'function') {
                    onExpenseSet();
                }

            } catch (error) {
                console.error(`Error deleting expense:`, error);
                const errorMessage = error.isBusinessLogicError
                    ? error.message
                    : error.message || 'An error occurred while deleting the expense';
                toast.error(errorMessage, {
                    description: "Please try again or contact support if the issue persists.",
                    duration: 4000
                });
            }
        };
                
        // Function which is rendering all fields
        const renderFormField = (field) => {
            const { id: fieldID, label, type, placeholder, required } = field;

            // Render textarea field
            if (type === 'textarea') {
                return (
                    <div key={fieldID} className="space-y-1.5">
                        <Label htmlFor={fieldID} required={required}>
                            {label}
                        </Label>
                        <Textarea
                            id={fieldID}
                            name={fieldID}
                            value={expenseFormData[fieldID]}
                            onChange={(e) => handleFieldChange(fieldID, e.target.value)}
                            placeholder={placeholder}
                            allowResize={false}
                            className="min-h-20 text-sm"
                        />
                    </div>
                );
            }

            // Render date field
            else if (type === 'date') {
                return (
                    <DatePicker
                        key={fieldID}
                        fieldID={fieldID}
                        label={label}
                        placeholder={placeholder}
                        required={required}
                        value={expenseFormData[fieldID] ? new Date(expenseFormData[fieldID]) : undefined}
                        onChange={(date) => handleFieldChange(fieldID, date ? format(date, 'dd-MMM-yy') : '')}
                        disabled={{ after: new Date() }}
                        defaultMonth={defaultMonthYearString}
                        isDisabled={isEditMode}
                    />
                )
            }

            // Render text/number input field
            else if (type === 'text') {
                return (
                    <div key={fieldID} className="space-y-1.5">
                        <Label htmlFor={fieldID} required={required}>
                            {label}
                        </Label>
                        <Input
                            id={fieldID}
                            name={fieldID}
                            value={expenseFormData[fieldID]}
                            onChange={(e) => handleFieldChange(fieldID, e.target.value)}
                            placeholder={placeholder}
                            type="text"
                            className="h-11 text-sm"
                        />
                    </div>
                );
            }

            else if (type === 'number') {
                return (
                    <div key={fieldID} className="space-y-1.5">
                        <Label htmlFor={fieldID} required={required}>
                            {label}
                        </Label>
                        <Input
                            id={fieldID}
                            name={fieldID}
                            value={expenseFormData[fieldID]}
                            onChange={(e) => handleFieldChange(fieldID, e.target.value)}
                            placeholder={placeholder}
                            type="text"
                            className="h-11 text-sm"
                        />
                    </div>
                );
            }

            // Render dropdown field
            else if (type === 'dropDown') {
                let options = [];
                if (fieldID === 'expenseTypeID') {
                    options = expenseCategories;
                }

                return (
                    <div className='space-y-1.5' key={fieldID}>
                        <Label htmlFor={fieldID} className={"cursor-pointer mb-1"} required={required}>
                            {label}
                        </Label>
                        <CustomSearchableDropdown
                            fieldID={fieldID}
                            required={required}
                            onSelect={(value, option) => handleDropdownChange(fieldID, option, value)}
                            options={options}
                            label={label}
                            placeholder={placeholder}
                            value={expenseFormData[fieldID]}
                            onChange={(val) => handleFieldChange(fieldID, val)}
                            emptyMessage="No categories found."
                            searchEnabled={false}
                        />
                    </div>
                );
            }
        };

        // Function which is rendering all form fields
        const renderDynamicFields = () => {
            const expenseFields = [
                {
                    id: 'expenseDT',
                    label: 'Date',
                    placeholder: new Date().toLocaleDateString(),
                    type: 'date',
                    required: true,
                    priority: 1
                },
                {
                    id: 'amount',
                    label: 'Amount',
                    placeholder: 'e.g. 15000',
                    type: 'number',
                    required: true,
                    priority: 2
                },
                {
                    id: 'expenseTypeID',
                    label: 'Category',
                    placeholder: 'Select Category',
                    type: 'dropDown',
                    required: true,
                    priority: 3
                },
                {
                    id: 'notes',
                    label: 'Notes',
                    placeholder: 'e.g. Purchased office supplies',
                    type: 'textarea',
                    required: false,
                    priority: 4
                }
            ];

            // Arrange fields in rows
            let fieldRows = [
                [expenseFields[0], expenseFields[1], expenseFields[2]], 
                [expenseFields[3]]  // Notes in separate row
            ];
            if (isMobile) {
                fieldRows = [
                    [expenseFields[0], expenseFields[1]], 
                    [expenseFields[2]],
                    [expenseFields[3]]
                ];
            }

            return fieldRows.map((row, rowIndex) => {
                let gridCols = row.length === 3 ? 'grid-cols-1 lg:grid-cols-3' : 'grid-cols-1';
                gridCols = row.length === 2 ? 'grid-cols-2' : gridCols;


                return (
                    <div key={`row-${rowIndex}`} className={`grid ${gridCols} gap-4 mb-1`}>
                        {row.map((field) => (
                            <div key={field.id}>
                                {renderFormField(field)}
                            </div>
                        ))}
                    </div>
                );
            });
        };

        // Rendering action buttons
        const renderActionButtons = () => {
            return (
                <div className="py-4">
                    <div className="w-full flex flex-col gap-4 sm:flex-row sm:justify-start sm:gap-3">
                        <Button
                            onClick={handleSaveExpense}
                            className="w-full sm:w-auto"
                        >
                            <Check className="h-4 w-4" strokeWidth={3} />
                            {prefilledData?.expenseID ? 'Update Expense' : 'Save Expense'}
                        </Button>
                        <Button
                            variant="outline"
                            onClick={() => setIsOpen(false)}
                            className="sahi-secondary-button flex-1 hidden sm:flex sm:flex-none sm:min-w-[100px]"
                        >
                            <X className="h-4 w-4  " />
                            Close
                        </Button>
                    </div>
                </div>
            );
        };

        // Create titles for mobile and desktop
        const mobileTitle = isMobile ? getMobileHeaderTitle(dialogTitle) : null;
        const desktopTitle = dialogTitle;

        // Rendering expense entry component
        return (
            <>
                {triggerButton && (
                    <Button onClick={() => setIsOpen(true)}>
                        <Plus className="h-4 w-4" />
                        {triggerText}
                    </Button>
                )}

                {!isExtFieldUse && isOpen && (
                    <Dialog open={isOpen} onOpenChange={setIsOpen}>
                        <DialogContent
                            onInteractOutside={(e) => e.preventDefault()}
                            className="lg:!max-w-[45vw] max-w-[100vw]"
                        >
                            <DialogHeader className="gap-0 px-3 sm:px-4 md:px-4">
                                <div className="sm:hidden flex items-center justify-between w-full h-8">
                                    <div className="flex items-center gap-2 flex-1 min-w-0">
                                        <DialogClose tabIndex={-1} className="p-0 w-auto h-auto flex-shrink-0 flex justify-center">
                                            <ArrowLeft className="h-6 w-6" />
                                            <span className="sr-only">Back</span>
                                        </DialogClose>
                                        <DialogTitle className="text-lg font-medium">
                                            <div className="truncate">{mobileTitle}</div>
                                        </DialogTitle>
                                        {prefilledData?.expenseID ? (
                                            <Button
                                                variant="ghost"
                                                size="default"
                                                className="p-2 w-auto h-auto hover:bg-white/10 hover:text-white"
                                                onClick={() => setIsDeleteAlertOpen(true)}
                                            >
                                                <Trash2 className="h-[20px] w-[20px]" />
                                            </Button>
                                        ) : null}
                                    </div>
                                </div>
                                <DialogTitle className="hidden sm:block text-lg md:text-xl">
                                    {desktopTitle}
                                </DialogTitle>
                                <div className="hidden sm:flex items-center gap-2">
                                    {prefilledData?.expenseID ? (
                                        <Button
                                            variant="ghost"
                                            size="default"
                                            className="p-2 w-auto h-auto sm:text-text-color sm:hover:bg-gray-200"
                                            onClick={() => setIsDeleteAlertOpen(true)}
                                        >
                                            <Trash2 className="h-[20px] w-[20px]" />
                                        </Button>
                                    ) : null}
                                    <DialogClose className="flex-shrink-0" tabIndex={-1}>
                                        <X className="h-5 w-5" />
                                        <span className="sr-only">Close</span>
                                    </DialogClose>
                                </div>
                            </DialogHeader>

                            <div className="px-4 flex-1 overflow-y-auto max-h-[calc(100vh-100px)] sm:max-h-[75vh]">
                                <div className="space-y-4">
                                    {renderDynamicFields()}
                                </div>
                                {renderActionButtons()}
                            </div>
                        </DialogContent>
                    </Dialog>
                )}

                {isExtFieldUse && isOpen && (
                    <div className={`${!extFieldUse && 'px-4'} flex-1 overflow-y-auto max-h-[calc(100vh-100px)] sm:max-h-[75vh]`}>
                        <div className="space-y-4">
                            {renderDynamicFields()}
                        </div>
                    </div>
                )}


               <AlertBox
                    open={isDeleteAlertOpen}
                    onOpenChange={(open) => setIsDeleteAlertOpen(open)}
                    title="Delete Expense"
                    description="Are you sure you wish to delete this expense?"
                    actions={[
                        { label: "No", value: 0, variant: "cancel", icon: <X className="h-4 w-4" /> },
                        { label: "Yes", value: 1, icon: <Check className="h-4 w-4" /> },
                    ]}
                    onAction={(val) => {
                        if (val === 1) {
                        handleDeleteExpense(); 
                        }
                        setIsDeleteAlertOpen(false);
                    }}
                />
            </>
        );
    });

export default ExpenseEntry;